/**
 * What the launcher says before the chat is opened: the label next to the icon and the one-sentence bubble for the
 * page the visitor is on. Texts come from the tenant theme (`launcher_label`, `launcher_teasers`, sent by the API with
 * the sector defaults) and fall back to the generic strings.
 */
import { fmt, type LauncherStrings as Strings } from '../shared/locale';
import { loadState, saveState } from '../shared/storage';
import type { TenantTheme } from '../types/contracts';
import type { Teaser, TeaserAction } from './launcher';

export type TeaserPage = 'product' | 'collection' | 'home' | 'default';

/** How long a bubble closed with its X stays away. */
export const TEASER_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
const SEEN_PREFIX = 'aicommerce:teaser:';

/** Page kind from the integration's `context.page_type` (Shopify `request.page_type`, Woo equivalents). */
export function teaserPage(pageType: string | undefined): TeaserPage | null {
  const t = (pageType ?? '').toLowerCase();
  if (t === 'product') return 'product';
  if (t === 'collection' || t === 'search' || t === 'list-collections' || t === 'category' || t === 'shop') return 'collection';
  if (t === 'index' || t === 'home' || t === 'front_page') return 'home';
  // the visitor is paying or managing the account: nothing to interrupt
  if (t === 'cart' || t === 'checkout' || t.startsWith('customers') || t === 'account') return null;
  return 'default';
}

export function launcherLabel(theme: TenantTheme | undefined, strings: Strings): string {
  return typeof theme?.launcher_label === 'string' ? theme.launcher_label.trim() : strings.launcherLabel;
}

/** Brand image for the launcher; only https URLs, anything else keeps the default icon. */
export function launcherIcon(theme: TenantTheme | undefined): string | null {
  const url = theme?.launcher_icon_url;
  return typeof url === 'string' && /^https:\/\//.test(url.trim()) ? url.trim() : null;
}

/** The car saved in the store configurator (`aca.fitment.vehicle`): its name and how many wheels fit it. */
export interface SavedVehicle {
  label: string;
  products: number;
}

const ACTION_DOS = new Set(['pick_vehicle', 'vehicle_results']);
const text = (v: unknown, max: number): string | undefined => (typeof v === 'string' && v.trim() ? v.trim().slice(0, max) : undefined);

function actionsOf(v: unknown): TeaserAction[] {
  if (!Array.isArray(v)) return [];
  return v.flatMap((a: unknown): TeaserAction[] => {
    const o = (a && typeof a === 'object' ? a : {}) as Record<string, unknown>;
    const label = text(o.label, 40);
    if (!label) return [];
    const message = text(o.message, 300);
    const act = typeof o.do === 'string' && ACTION_DOS.has(o.do) ? (o.do as TeaserAction['do']) : undefined;
    return [{ label, ...(message ? { message } : {}), ...(act ? { do: act } : {}) }];
  }).slice(0, 2);
}

function fromTheme(v: unknown): Teaser | null {
  if (typeof v === 'string') return v.trim() ? { text: v.trim() } : null;
  if (v && typeof v === 'object' && typeof (v as Teaser).text === 'string' && (v as Teaser).text.trim()) {
    const message = text((v as Teaser).message, 300);
    const actions = actionsOf((v as Teaser).actions);
    return { text: (v as Teaser).text.trim(), ...(message ? { message } : {}), ...(actions.length ? { actions } : {}) };
  }
  return null;
}

/** Saved car with a positive count of compatible wheels (the count comes from the fitment API, never from the model). */
export function readSavedVehicle(): SavedVehicle | null {
  try {
    const v = JSON.parse(window.localStorage.getItem('aca.fitment.vehicle') ?? 'null') as Record<string, unknown> | null;
    const label = text(v?.label, 60);
    const products = typeof v?.products === 'number' ? Math.floor(v.products) : 0;
    return label && products > 0 ? { label, products } : null;
  } catch {
    return null;
  }
}

/**
 * The bubble for this page, or null when the shop turned it off (`launcher_teaser: false`) or the page has none.
 * A visitor with a saved car gets the `vehicle` bubble (`{vehicle}`, `{count}` filled in) except on a product page,
 * where the configurator badge already answers for that car.
 */
export function pickTeaser(theme: TenantTheme | undefined, strings: Strings, pageType: string | undefined, vehicle: SavedVehicle | null = null): Teaser | null {
  if (theme?.launcher_teaser === false) return null;
  const page = teaserPage(pageType);
  if (!page) return null;
  const own = theme?.launcher_teasers && typeof theme.launcher_teasers === 'object' ? (theme.launcher_teasers as Record<string, unknown>) : {};
  const forCar = vehicle && page !== 'product' ? fromTheme(own.vehicle) : null;
  if (forCar && vehicle) return { ...forCar, text: fmt(forCar.text, { vehicle: vehicle.label, count: vehicle.products }) };
  return fromTheme(own[page]) ?? fromTheme(own.default) ?? { text: page === 'product' ? strings.teaserProduct : strings.teaserDefault };
}

/** Once per browser tab session, never to a visitor who already has a conversation or closed the bubble recently. */
export function teaserAllowed(tenantKey: string, now = Date.now()): boolean {
  const state = loadState(tenantKey);
  if (state.conversation_id) return false;
  if (typeof state.teaser_dismissed_at === 'number' && now - state.teaser_dismissed_at < TEASER_SNOOZE_MS) return false;
  try {
    return window.sessionStorage.getItem(SEEN_PREFIX + tenantKey) === null;
  } catch {
    return false; // no storage: the bubble would come back on every page
  }
}

export function markTeaserSeen(tenantKey: string): void {
  try {
    window.sessionStorage.setItem(SEEN_PREFIX + tenantKey, '1');
  } catch {
    /* ignore */
  }
}

export function snoozeTeaser(tenantKey: string, now = Date.now()): void {
  saveState(tenantKey, { teaser_dismissed_at: now });
}
